import {TTypographyVariant, Typography} from 'components/Typography';
import {CSSProperties, FC, useMemo} from 'react';
import styles from './style.module.scss';
import classNames from 'classnames';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {useProductDiscount} from 'hooks/product/useProductDiscount';
import {selectProductData} from 'modules/product-card/selectors';
import {ICartData} from 'modules/cart/models';
import {useGetUserQuery} from 'modules/user/services';
import cs from 'classnames';
import {TUserGroupName} from 'modules/user/models';
import {USER_GROUPS_CODE} from 'modules/user/constants';

interface IStatus {
    groups: readonly number[];
    title: string;
}

interface IProps {
    type?: 'small' | 'extra-small';
    borderRadius?: string;
    height?: string;
    variant?: TTypographyVariant;
    style?: CSSProperties;
    discountValue?: number;
    cartData?: Pick<ICartData, 'isEcom' | 'ecomName'>;
    isAddToCartNotification?: boolean;
    className?: string;
}

interface IRenderData {
    className: string;
    text: JSX.Element;
}

const STATUS: {
    default: IStatus;
    employee: IStatus;
    prive: Record<'bronze' | 'silver' | 'gold' | 'platinum', IStatus>;
    ecom: Record<'white' | 'grey' | 'black' | 'bordo', IStatus>;
} = {
    default: {
        groups: [],
        title: '',
    },
    // SIMPLE TEAM
    employee: {
        groups: USER_GROUPS_CODE.employee,
        title: 'Team',
    },
    // PRIVE
    prive: {
        bronze: {
            groups: [40],
            title: 'Bronze',
        },
        silver: {
            groups: [41],
            title: 'Silver',
        },
        gold: {
            groups: [42],
            title: 'Gold',
        },
        platinum: {
            groups: [43],
            title: 'Platinum',
        },
    },
    // ECOM
    ecom: {
        white: {
            groups: [50],
            title: 'White',
        },
        grey: {
            groups: [51],
            title: 'Grey',
        },
        black: {
            groups: [52],
            title: 'Black',
        },
        bordo: {
            groups: [52],
            title: 'Black',
        },
    },
};

const createDiscountText = (discount: number, statusTitle?: string): JSX.Element => (
    <>
        -{discount}%{statusTitle && <span className={styles.status}> {statusTitle}</span>}
    </>
);

const createClassName = (type: string, statusClass: string): string =>
    classNames(styles.main, styles[type], styles[statusClass]);

const getStatusClass = (isEcomRule: boolean, userStyle?: string, userGroupName?: string): string => {
    if ('employee' === userGroupName) {
        return userGroupName;
    }

    if (isEcomRule) {
        return userStyle ?? 'default';
    }

    return 'default';
};

const getStatusTitle = (statusClass: string, userGroupName?: TUserGroupName): string => {
    if ('employee' === userGroupName) {
        return STATUS.employee.title;
    }

    if (!userGroupName) {
        return '';
    }

    const statuses = STATUS[userGroupName as keyof typeof STATUS];

    if (statuses && 'object' === typeof statuses && statusClass in statuses) {
        return (statuses as any)[statusClass].title;
    }

    return '';
};

const renderProductData = (
    discount: number,
    type: string,
    isEcomRule: boolean,
    userStyle?: string,
    userGroupName?: TUserGroupName,
): IRenderData => {
    const statusClass = getStatusClass(isEcomRule, userStyle, userGroupName);
    const statusTitle = getStatusTitle(statusClass, userGroupName);

    return {
        className: createClassName(type, statusClass),
        text: createDiscountText(discount, statusTitle),
    };
};

const renderCartData = (
    discount: number,
    type: string,
    cartData: Pick<ICartData, 'isEcom' | 'ecomName'>,
    userStyle?: string,
    isAddNotification = false,
): IRenderData => {
    // Не ecom
    if (!cartData.isEcom) {
        return {
            className: classNames(styles.main, styles[type]),
            text: createDiscountText(discount),
        };
    }

    // EcomName пустой
    if ('' === cartData.ecomName) {
        return {
            className: classNames(styles.main, styles.simple, styles[type]),
            text: createDiscountText(discount, STATUS.employee.title),
        };
    }

    // EcomName не пустой
    return {
        className: classNames(styles.main, styles[userStyle ?? 'default'], styles[type]),
        text: createDiscountText(discount, isAddNotification ? cartData.ecomName : undefined),
    };
};

export const DiscountPercentLabel: FC<IProps> = ({
    type = '',
    borderRadius,
    height,
    variant = 'label-16-r',
    style = {},
    discountValue,
    cartData,
    isAddToCartNotification = false,
    className,
}) => {
    const productData = useAppSelector(selectProductData);
    const {data: user} = useGetUserQuery();
    const productDiscount = useProductDiscount(productData);

    const discount = discountValue ?? productDiscount;
    const isEcomRule = !productData?.price.showDiscountPercent;
    const userStyle = user?.style;
    const userGroupName = user?.userGroupName;

    const renderData = useMemo((): IRenderData | null => {
        if (!discount) {
            return null;
        }

        // логика для продукта (не корзина)
        if (!cartData && productData) {
            return renderProductData(discount, type, isEcomRule, userStyle, userGroupName);
        }

        // Логика для нотификации / корзины
        if (cartData) {
            return renderCartData(discount, type, cartData, userStyle, isAddToCartNotification);
        }

        return null;
    }, [discount, cartData, productData, type, isEcomRule, userStyle, userGroupName, isAddToCartNotification]);

    if (!renderData?.text) {
        return null;
    }

    return (
        <div style={{borderRadius, height, ...style}} className={cs(renderData.className, className)}>
            <Typography className={styles.text} variant={variant}>
                {renderData.text}
            </Typography>
        </div>
    );
};
